/**
 * Report definitions.
 *
 * A report is data, not a screen: an id, the columns it shows, and a `rows(book)` that derives them
 * from the store. `ReportsSection` renders any of them, so adding a report is one entry here rather
 * than another component.
 *
 * Every report reads the same book the screens read, so a report can never disagree with the table
 * it was run from.
 */

import { FUNDS, TRANSACTION_KIND_LABELS, SEGMENT_LABELS } from '../data/seed';
import { MATCH_STATUS } from '../redux/portfolioSlice';

const money = (value) => Number(value || 0).toFixed(2);
const units = (value) => Number(value || 0).toFixed(4);

/** Account id → account, for joining transactions back to their client. */
const accountIndex = (accounts) =>
    accounts.reduce((acc, account) => ({ ...acc, [account.accountId]: account }), {});

const MATCH_LABELS = {
    [MATCH_STATUS.AUTO]: 'Auto reconciled',
    [MATCH_STATUS.MANUAL_APPROVED]: 'Manually reconciled',
    [MATCH_STATUS.MANUAL_PENDING]: 'Awaiting approval',
    [MATCH_STATUS.UNRECONCILED]: 'Unreconciled'
};

/* ── shared report bodies ────────────────────────────────────────────────────────────────────── */

/** One row per account per fund held — the holdings position at today's price. */
const holdingsRows = ({ accounts, fundPrices }) =>
    accounts.flatMap((account) =>
        account.strategies.flatMap((strategy) =>
            FUNDS.filter((fund) => Number(strategy.holdings?.[fund.fundId] || 0) > 0).map((fund) => {
                const held = Number(strategy.holdings[fund.fundId]);
                return {
                    key: `${strategy.strategyId}:${fund.fundId}`,
                    accountId: account.accountId,
                    accountName: account.accountName,
                    segment: SEGMENT_LABELS[account.segment] || account.segment,
                    strategyName: strategy.strategyName,
                    activationCode: strategy.activationCode,
                    fundId: fund.fundId,
                    units: units(held),
                    unitPrice: money(fundPrices[fund.fundId]),
                    value: money(held * fundPrices[fund.fundId])
                };
            })
        )
    );

/** One row per transaction leg, with the client it belongs to. */
const transactionRows = ({ accounts, transactions }) => {
    const byId = accountIndex(accounts);

    return transactions.map((txn) => ({
        key: txn.transactionId,
        transactionId: txn.transactionId,
        accountName: byId[txn.accountId]?.accountName || txn.accountId,
        accountId: txn.accountId,
        segment: SEGMENT_LABELS[byId[txn.accountId]?.segment] || '—',
        kind: TRANSACTION_KIND_LABELS[txn.kind] || txn.kind,
        transactionType: txn.transactionType,
        fundId: txn.fundId,
        strategyName: txn.strategyName,
        status: txn.status,
        reconciled: txn.reconciled ? 'Yes' : 'No',
        unitPrice: money(txn.unitPrice),
        units: units(txn.units),
        amount: money(txn.amount),
        transactionDate: txn.transactionDate
    }));
};

/* ── column sets ─────────────────────────────────────────────────────────────────────────────── */

const HOLDING_COLUMNS = [
    { key: 'accountName', title: 'Client' },
    { key: 'accountId', title: 'Account ID' },
    { key: 'strategyName', title: 'Strategy' },
    { key: 'activationCode', title: 'Activation code' },
    { key: 'fundId', title: 'Fund' },
    { key: 'units', title: 'Units', align: 'right' },
    { key: 'unitPrice', title: 'Unit price', align: 'right' },
    { key: 'value', title: 'Value (LKR)', align: 'right' }
];

const TRANSACTION_COLUMNS = [
    { key: 'transactionId', title: 'Transaction ID', mono: true },
    { key: 'accountName', title: 'Client' },
    { key: 'kind', title: 'Transaction' },
    { key: 'transactionType', title: 'Type' },
    { key: 'fundId', title: 'Fund' },
    { key: 'strategyName', title: 'Strategy' },
    { key: 'unitPrice', title: 'Unit price', align: 'right' },
    { key: 'units', title: 'Units', align: 'right' },
    { key: 'amount', title: 'Value (LKR)', align: 'right' },
    { key: 'status', title: 'Status' },
    { key: 'reconciled', title: 'Reconciled' },
    { key: 'transactionDate', title: 'Date' }
];

/* ── wealth management desk ──────────────────────────────────────────────────────────────────── */

export const WEALTH_REPORTS = [
    {
        id: 'wm-holdings',
        label: 'Client holdings',
        description:
            'Every wealth management client’s position, one row per strategy per fund, valued at today’s unit price.',
        fileName: 'wm_client_holdings',
        columns: HOLDING_COLUMNS,
        rows: holdingsRows
    },
    {
        id: 'wm-transactions',
        label: 'Client transactions',
        description:
            'Every transaction raised for a wealth management client, pending and approved, with its settlement figures.',
        fileName: 'wm_client_transactions',
        columns: TRANSACTION_COLUMNS,
        rows: transactionRows
    },
    {
        id: 'wm-portfolio',
        label: 'Portfolio valuation',
        description:
            'One row per client: what they hold across all strategies, and what it is worth right now.',
        fileName: 'wm_portfolio_valuation',
        columns: [
            { key: 'accountName', title: 'Client' },
            { key: 'accountId', title: 'Account ID' },
            { key: 'accountType', title: 'Type' },
            { key: 'strategyCount', title: 'Strategies', align: 'right' },
            ...FUNDS.map((fund) => ({ key: `${fund.fundId}_units`, title: `${fund.fundId} units`, align: 'right' })),
            { key: 'totalValue', title: 'Total value (LKR)', align: 'right' }
        ],
        rows: ({ accounts, fundPrices }) =>
            accounts.map((account) => {
                const row = {
                    key: account.accountId,
                    accountId: account.accountId,
                    accountName: account.accountName,
                    accountType: account.accountType,
                    strategyCount: account.strategies.length,
                    totalValue: 0
                };

                FUNDS.forEach((fund) => {
                    const held = account.strategies.reduce(
                        (sum, strategy) => sum + Number(strategy.holdings?.[fund.fundId] || 0),
                        0
                    );
                    row[`${fund.fundId}_units`] = units(held);
                    row.totalValue += held * fundPrices[fund.fundId];
                });

                row.totalValue = money(row.totalValue);
                return row;
            })
    }
];

/* ── unit registry (whole book, both desks) ──────────────────────────────────────────────────── */

export const REGISTRY_REPORTS = [
    {
        id: 'reg-holdings',
        label: 'Unit holding',
        description:
            'The unit register: every holding on the book, both desks, one row per strategy per fund.',
        fileName: 'unit_holding_report',
        columns: [{ key: 'segment', title: 'Desk' }, ...HOLDING_COLUMNS],
        rows: holdingsRows
    },
    {
        id: 'reg-transactions',
        label: 'Transactions',
        description: 'Every transaction on the book, both desks, pending and approved.',
        fileName: 'transaction_report',
        columns: [
            TRANSACTION_COLUMNS[0],
            { key: 'segment', title: 'Desk' },
            ...TRANSACTION_COLUMNS.slice(1)
        ],
        rows: transactionRows
    },
    {
        id: 'reg-fund-summary',
        label: 'Fund summary',
        description:
            'Per fund: units in issue across the whole book, the current unit price, and the resulting NAV.',
        fileName: 'fund_summary_report',
        columns: [
            { key: 'fundId', title: 'Fund' },
            { key: 'fundName', title: 'Fund name' },
            { key: 'holders', title: 'Accounts holding', align: 'right' },
            { key: 'unitsInIssue', title: 'Units in issue', align: 'right' },
            { key: 'unitPrice', title: 'Unit price', align: 'right' },
            { key: 'navValue', title: 'NAV value (LKR)', align: 'right' }
        ],
        rows: ({ accounts, fundPrices }) =>
            FUNDS.map((fund) => {
                const unitsInIssue = accounts.reduce(
                    (sum, account) =>
                        sum +
                        account.strategies.reduce(
                            (s, strategy) => s + Number(strategy.holdings?.[fund.fundId] || 0),
                            0
                        ),
                    0
                );
                const holders = accounts.filter((account) =>
                    account.strategies.some((strategy) => Number(strategy.holdings?.[fund.fundId] || 0) > 0)
                ).length;

                return {
                    key: fund.fundId,
                    fundId: fund.fundId,
                    fundName: fund.fundName,
                    holders,
                    unitsInIssue: units(unitsInIssue),
                    unitPrice: money(fundPrices[fund.fundId]),
                    navValue: money(unitsInIssue * fundPrices[fund.fundId])
                };
            })
    },
    {
        id: 'reg-reconciliation',
        label: 'Reconciliation',
        description:
            'Every bank statement line with how it was matched — auto, manual, awaiting approval, or not at all.',
        fileName: 'reconciliation_report',
        columns: [
            { key: 'referenceId', title: 'Reference', mono: true },
            { key: 'matchedTransactionId', title: 'Transaction ID', mono: true },
            { key: 'matchStatus', title: 'Status' },
            { key: 'amount', title: 'Amount (LKR)', align: 'right' },
            { key: 'valueDate', title: 'Value date' },
            { key: 'description', title: 'Description' },
            { key: 'comment', title: 'Comment' }
        ],
        rows: ({ statement }) =>
            statement.map((line) => ({
                key: line.id,
                referenceId: line.referenceId,
                matchedTransactionId: line.matchedTransactionId || '—',
                matchStatus: MATCH_LABELS[line.matchStatus] || line.matchStatus,
                amount: money(line.amount),
                valueDate: line.valueDate,
                description: line.description || '',
                comment: line.comment || ''
            }))
    }
];

/** RFC-4180 enough for a prototype: quote everything, double any embedded quotes. */
export const toCsv = (columns, rows) => {
    const cell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    return [
        columns.map((column) => cell(column.title)).join(','),
        ...rows.map((row) => columns.map((column) => cell(row[column.key])).join(','))
    ].join('\n');
};
